import { CHARACTERS, RACIALS } from '../data/characters';
import { PLAYER_BASE_HP, PLAYER_MOVE_SPEED } from '../physics/constants';
import {
  CHARACTER_IDS,
  type CharacterDef,
  type CharacterId,
  type RacialDef,
  type SlotKey,
  type SpellId,
} from '../types';
import { MIN_SPELL_COOLDOWN_MS, spellStats } from './spells';

/** A character's base stats and racial, as the sim sees them. */
export interface CharacterMods {
  maxHp: number;
  moveSpeed: number;
  racial: RacialDef | null;
}

/** No racial, default stats: racials off, an unknown id, or a cosmetic-only character. */
export const BASELINE_MODS: Readonly<CharacterMods> = Object.freeze({
  maxHp: PLAYER_BASE_HP,
  moveSpeed: PLAYER_MOVE_SPEED,
  racial: null,
});

function isCharacterId(value: string): value is CharacterId {
  return (CHARACTER_IDS as readonly string[]).includes(value);
}

/**
 * The only reader of `CHARACTERS[...].racial` and `stats`. With `racialsOn` false every character
 * gets the baseline, stat overrides included.
 */
export function characterMods(character: string, racialsOn: boolean): CharacterMods {
  if (!racialsOn || !isCharacterId(character)) return BASELINE_MODS;
  const def: CharacterDef = CHARACTERS[character];
  return {
    maxHp: def.stats?.maxHp ?? PLAYER_BASE_HP,
    moveSpeed: def.stats?.moveSpeed ?? PLAYER_MOVE_SPEED,
    racial: def.racial === null ? null : RACIALS[def.racial],
  };
}

/**
 * The cooldown a cast from `slot` starts: the spell's cooldown with its rune and level, times the
 * racial's `cooldownMult` for that slot (Arcane Recall, applied last: Quickstep L1 7 s → 6.3 s),
 * rounded to whole ms and never below MIN_SPELL_COOLDOWN_MS. The server's `Combat.cast` and the
 * HUD's sweep both use this, so the sweep reaches 0 exactly when a recast is allowed. Free casts
 * (Pactfire) are separate: see `freeCastsPerRound`.
 */
export function effectiveCooldownMs(
  spellId: SpellId,
  level: number,
  slot: SlotKey,
  mods: CharacterMods,
  rune?: string | null,
): number {
  const base = spellStats(spellId, level, rune).cooldownMs;
  const racial = mods.racial;
  const mult = racial?.kind === 'passive' ? (racial.cooldownMult?.[slot] ?? 1) : 1;
  if (mult === 1) return base;
  return Math.max(MIN_SPELL_COOLDOWN_MS, Math.round(base * mult));
}

/**
 * Casts per round that skip the cooldown (Pactfire), summed over slots: the round-start value of
 * `racialCharges`. 0 without such a racial.
 */
export function freeCastsPerRound(mods: CharacterMods): number {
  const racial = mods.racial;
  if (racial?.kind !== 'passive' || !racial.freeCastsPerRound) return 0;
  let total = 0;
  for (const n of Object.values(racial.freeCastsPerRound)) total += n ?? 0;
  return total;
}

/** True when a cast from `slot` may spend one of the player's free casts (`racialCharges`). */
export function isFreeCastSlot(mods: CharacterMods, slot: SlotKey): boolean {
  const racial = mods.racial;
  return racial?.kind === 'passive' && (racial.freeCastsPerRound?.[slot] ?? 0) > 0;
}

/** The racial's `active` variant (Stealth), or null. */
export function activeRacial(mods: CharacterMods): Extract<RacialDef, { kind: 'active' }> | null {
  return mods.racial?.kind === 'active' ? mods.racial : null;
}

/** The racial's guard variant (Stone Guard), or null for a passive racial or none. */
export function guardRacial(mods: CharacterMods): Extract<RacialDef, { kind: 'guard' }> | null {
  return mods.racial?.kind === 'guard' ? mods.racial : null;
}

/**
 * Whether a guard facing `guardFacing` (radians) covers a hit that pushes along (pushX, pushY).
 * The threat always comes from the side opposite the push, so one rule serves every source:
 * projectiles (reverse of their velocity), Lightning (from the caster), Meteor (from the blast
 * centre), Thrust (from the dasher) and collisions (from the pusher). No direction → unguarded.
 */
export function guardCovers(
  guardFacing: number,
  pushX: number,
  pushY: number,
  arcDeg: number,
): boolean {
  const len = Math.hypot(pushX, pushY);
  if (len === 0) return false;
  const dot = -(pushX * Math.cos(guardFacing) + pushY * Math.sin(guardFacing)) / len;
  return dot >= Math.cos(((arcDeg / 2) * Math.PI) / 180);
}

/**
 * Walk-speed multiplier from a raised guard (Stone Guard ×0.5), else 1. The server folds it into
 * movement; client prediction and bots read the synced `guardUntil` against server time.
 */
export function guardSpeedMult(
  character: string,
  racialsOn: boolean,
  guardUntil: number,
  now: number,
): number {
  if (!(guardUntil > now)) return 1;
  return guardRacial(characterMods(character, racialsOn))?.moveSpeedMult ?? 1;
}
